'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  id: string;
  label: string;
  href: string;
  offset: number;
}

interface HeaderProps {
  onNavigate?: (sectionId: string) => void;
}

const Header = ({ onNavigate }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems: NavigationItem[] = [
    { id: 'personal-financing', label: 'Kredyt Hipoteczny', href: '#personal-financing', offset: 80 },
    { id: 'calculator', label: 'Kalkulator', href: '#calculator', offset: 80 },
    { id: 'about', label: 'O Nas', href: '#about', offset: 80 },
    { id: 'contact', label: 'Kontakt', href: '#contact', offset: 80 },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      const sections = navigationItems.map(item => ({
        id: item.id,
        element: document.getElementById(item.id),
      }));

      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: NavigationItem) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    const element = document.getElementById(item.id);
    if (element) {
      const offsetPosition = element.offsetTop - item.offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      if (onNavigate) {
        onNavigate(item.id);
      }
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-250 ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between h-20 lg:h-[80px]">
            <Link
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center gap-3 transition-opacity duration-250 hover:opacity-80"
            >
              <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary-foreground"
                  />
                  <path
                    d="M2 17L12 22L22 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary-foreground"
                  />
                  <path
                    d="M2 12L12 17L22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary-foreground"
                  />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-headline font-bold text-primary leading-tight">
                  Nasza Hipoteka
                </span>
                <span className="text-xs text-muted-foreground leading-tight">
                  Ekspert Finansowy
                </span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navigationItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`text-sm font-body font-medium transition-colors duration-250 hover:text-primary ${
                    activeSection === item.id ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:+48667009004"
                className="flex items-center gap-2 text-sm font-body font-medium text-foreground transition-colors duration-250 hover:text-primary"
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>667 009 004</span>
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const contactSection = navigationItems.find(item => item.id === 'contact');
                  if (contactSection) {
                    handleNavClick(e as any, contactSection);
                  }
                }}
                className="px-6 py-2.5 bg-accent text-accent-foreground font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90 hover:shadow-lg"
              >
                Umów konsultację
              </a>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-foreground transition-colors duration-250 hover:text-primary"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                variant="outline"
              />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-background lg:hidden"
          style={{ top: '60px' }}
        >
          <nav className="flex flex-col p-6 space-y-1">
            {navigationItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item)}
                className={`px-4 py-3 text-base font-body font-medium rounded-lg transition-colors duration-250 ${
                  activeSection === item.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                {item.label}
              </a>
            ))}

            <div className="pt-6 mt-6 border-t border-border space-y-3">
              <a
                href="tel:+48667009004"
                className="flex items-center gap-3 px-4 py-3 text-base font-body font-medium text-foreground rounded-lg transition-colors duration-250 hover:bg-muted"
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>667 009 004</span>
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const contactSection = navigationItems.find(item => item.id === 'contact');
                  if (contactSection) {
                    handleNavClick(e as any, contactSection);
                  }
                }}
                className="block w-full px-6 py-3 bg-accent text-accent-foreground text-center font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90"
              >
                Umów konsultację
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;